import { Suspense, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { parseIsoZonedDateTime, formatZonedDateTime } from '@portal/pkg-format/date'
import { useHasEnablement } from '@portal/pkg-authorization'
import type { RoleDetailResponse, RoleUserResponse } from '@portal/pkg-api/gen/admin-console'
import { UserStatus } from '@portal/pkg-api/gen/admin-console'
import { AddUsersDialog } from './AddUsersDialog'
import { isMembershipEditable } from '../model/roleAccess'
import { useRemoveRoleMember } from '../mutations'
import { RoleQueries } from '../queries'

const MEMBERSHIP_REQUIRED_TYPE =
  'https://gomercatus.com/problems/admin/roles/role-membership-required'

function StatusChip({ status }: { status: UserStatus }) {
  const { t } = useTranslation('admin-console')
  const isEnabled = status === UserStatus.ENABLED
  return (
    <Chip
      label={t(isEnabled ? 'users.table.status.enabled' : 'users.table.status.disabled')}
      color={isEnabled ? 'success' : 'default'}
      size="small"
      variant="outlined"
    />
  )
}

/** Null for the Lite role, whose membership is every enabled user rather than rows anyone wrote. */
function DateAdded({ value }: { value?: string | null }) {
  const { i18n } = useTranslation()
  if (!value) return <>{'\u2014'}</>
  return <>{formatZonedDateTime(parseIsoZonedDateTime(value), i18n.language)}</>
}

function matches(user: RoleUserResponse, term: string) {
  if (!term) return true
  return (
    `${user.givenName} ${user.familyName}`.toLowerCase().includes(term) ||
    user.email.toLowerCase().includes(term)
  )
}

export function UsersTab({ role }: { role: RoleDetailResponse }) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [adding, setAdding] = useState(false)
  const [pendingRemove, setPendingRemove] = useState<RoleUserResponse | null>(null)

  const { data: members } = useSuspenseQuery(RoleQueries.members(role.id))
  const removeMember = useRemoveRoleMember()
  const canManage = useHasEnablement('Role', 'MANAGE', { global: true })
  const editable = canManage && isMembershipEditable(role.name)

  const visible = members.filter((user) => matches(user, search.toLowerCase().trim()))

  const confirmRemove = () => {
    if (!pendingRemove) return
    const user = pendingRemove
    removeMember.mutate(
      { id: role.id, userId: user.id },
      {
        onSuccess: () => {
          enqueueSnackbar(t('roles.users.userRemoved'), { variant: 'success' })
          setPendingRemove(null)
        },
        onError: (error: unknown) => {
          const axiosError = error as { response?: { data?: { type?: string } } }
          // The server refuses to leave a user holding no role at all.
          const key =
            axiosError.response?.data?.type === MEMBERSHIP_REQUIRED_TYPE
              ? 'roles.users.membershipRequired'
              : 'roles.users.removeFailed'
          enqueueSnackbar(t(key), { variant: 'error' })
          setPendingRemove(null)
        },
      },
    )
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          size="small"
          variant="outlined"
          placeholder={t('roles.users.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ '& .MuiOutlinedInput-root': { height: 32 }, width: 280 }}
        />
        {editable && (
          <Button variant="contained" size="small" onClick={() => setAdding(true)}>
            {t('roles.users.addUsers')}
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ width: '100%' }}>
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell>{t('roles.users.columns.name')}</TableCell>
              <TableCell>{t('roles.users.columns.email')}</TableCell>
              <TableCell sx={{ width: '15%' }}>{t('roles.users.columns.status')}</TableCell>
              <TableCell sx={{ width: '15%' }}>{t('roles.users.columns.licenseType')}</TableCell>
              <TableCell sx={{ width: '20%' }}>{t('roles.users.columns.dateAdded')}</TableCell>
              <TableCell sx={{ width: 56 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  {t(members.length === 0 ? 'roles.users.noMembers' : 'roles.users.noResults')}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    {t('displayName', {
                      givenName: user.givenName,
                      familyName: user.familyName,
                    })}
                  </TableCell>
                  <TableCell>{user.email.toLowerCase()}</TableCell>
                  <TableCell>
                    <StatusChip status={user.status} />
                  </TableCell>
                  <TableCell>{user.licenseType}</TableCell>
                  <TableCell>
                    <DateAdded value={user.dateAdded} />
                  </TableCell>
                  <TableCell align="right">
                    {editable && (
                      <Tooltip title={t('roles.users.remove')}>
                        <IconButton
                          size="small"
                          aria-label={t('roles.users.remove')}
                          onClick={() => setPendingRemove(user)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {adding && (
        <Suspense
          fallback={
            <Stack sx={{ alignItems: 'center', py: 2 }}>
              <CircularProgress />
            </Stack>
          }
        >
          <AddUsersDialog roleId={role.id} onClose={() => setAdding(false)} />
        </Suspense>
      )}

      <Dialog open={pendingRemove !== null} onClose={() => setPendingRemove(null)}>
        <DialogTitle>{t('roles.users.removeTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('roles.users.removeConfirm', {
              name: pendingRemove
                ? t('displayName', {
                    givenName: pendingRemove.givenName,
                    familyName: pendingRemove.familyName,
                  })
                : '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemove(null)}>{t('roles.users.cancel')}</Button>
          <Button variant="contained" color="error" onClick={confirmRemove}>
            {t('roles.users.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
